'use client';
import { cva, VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import React, { ButtonHTMLAttributes, ReactNode, CSSProperties } from 'react';

const buttonClasses = cva(
  'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-bg-button-primary text-text-primary hover:bg-opacity-90 focus:ring-border-primary-accent',
        secondary: 'bg-bg-button-secondary text-text-primary hover:bg-opacity-80 focus:ring-border-border-primary',
        outline: 'border-2 border-border-primary-accent text-text-accent bg-transparent hover:bg-bg-button-secondary focus:ring-border-primary-accent',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
      },
      size: {
        small: 'text-xs px-3 py-1.5',
        medium: 'text-base px-4 py-2',
        large: 'text-lg px-6 py-3',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonClasses> {
  text?: string;
  text_font_size?: string;
  text_font_family?: string;
  text_font_weight?: string;
  text_line_height?: string;
  text_text_align?: string;
  text_color?: string;
  fill_background_color?: string;
  border_border_radius?: string;
  border_border?: string;
  layout_width?: string;
  padding?: string;
  position?: string;
  children?: ReactNode;
}

const Button = ({
  text = "Contact us",
  text_font_size = "text-base",
  text_font_family = "Poppins",
  text_font_weight = "font-semibold",
  text_line_height = "leading-lg",
  text_text_align = "left",
  text_color = "text-text-primary",
  fill_background_color = "bg-primary-accent",
  border_border_radius = "rounded-lg",
  border_border,
  layout_width,
  padding,
  position,
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = 'button',
  ...props
}: ButtonProps) => {

  const hasValidBorder = border_border && border_border.trim() !== '';
  const hasValidWidth = layout_width && layout_width.trim() !== '';
  const hasValidPadding = padding && padding.trim() !== '';
  const hasValidPosition = position && position.trim() !== '';

  const optionalClasses = [
    hasValidBorder ? `border-[${border_border}]` : '',
    hasValidWidth ? `w-[${layout_width}]` : '',
    hasValidPadding ? padding : '',   // <-- Use padding directly
    hasValidPosition ? position : '',
  ].filter(Boolean).join(' ');

  const customStyles: CSSProperties = {
    ...(text_font_family && !text_font_family.startsWith('font-') && { fontFamily: text_font_family }),
  };

  const styleClasses = [
    text_font_size,
    text_font_family.startsWith('font-') ? text_font_family : '',
    text_font_weight,
    text_line_height,
    text_text_align === 'center' ? 'text-center' : text_text_align === 'right' ? 'text-right' : 'text-left',
    text_color,
    !variant ? fill_background_color : '',
    border_border_radius,
  ].filter(Boolean).join(' ');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    if (typeof onClick === 'function') {
      onClick(event);
    }
  }

  return (
    <button data-component-id="src/components/ui/Button.tsx:108:4" data-component-path="src/components/ui/Button.tsx" data-component-line="108" data-component-end-line="123" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      disabled={disabled}
      onClick={handleClick}
      style={customStyles}
      className={twMerge(
        buttonClasses({ variant, size }),
        styleClasses,
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children || text}
    </button>
  )
}

export default Button;
